import React, { createContext, useContext, useState, useEffect } from "react";
import { URL } from "../helper/Mainurl";

// Create context with default undefined
const NotificationContext = createContext(undefined);

// Custom hook
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [readCount, setReadCount] = useState(0);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [filter, setFilter] = useState("all");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [paginationData, setPaginationData] = useState(null);

  const token = localStorage.getItem("authToken");

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${URL}/v1/notification/list`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch notifications");
      const result = await response.json();
      const data = result.data || [];

      setNotifications(data?.notifications);
      setPaginationData(data?.pagination);

      const unread = data?.unread_count;
      const read = data?.stats?.read_notifications;
      const total = data?.stats?.total_notifications;

      setUnreadCount(unread);
      setReadCount(read);
      setTotalNotifications(total);
    } catch (err) {
      setError(err?.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const markNotificationAsRead = async (id) => {
    try {
      if (!token) throw new Error("No auth token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/v1/admin/notifications/mark-read`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ type: "", notification_id: id }),
        }
      );

      if (!response.ok) throw new Error("Failed to mark notification as read");

      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === id ? { ...notif, is_read: true } : notif
        )
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      if (!token) throw new Error("No auth token found");

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/v1/admin/notifications/mark-read`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ type: "all", notification_id: "" }),
        }
      );

      if (!response.ok) throw new Error("Failed to mark all as read");

      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filter, pageNo, limit]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        setNotifications,
        loading,
        error,
        refetch: fetchNotifications,
        unreadCount,
        readCount,
        filter,
        setFilter,
        pageNo,
        setPageNo,
        limit,
        setLimit,
        markNotificationAsRead,
        markAllAsRead,
        totalNotifications,
        paginationData,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
